// TeamCardListModal.jsx
import React, { useState, useEffect, useCallback } from 'react';
import TeamDarkButtonWithIcon from "team/shared/components/button/TeamDarkButtonWithIcon";
import { AddIcon, KeyboardArrowDownOutlinedIcon, VisibilityOutlinedIcon, ContactsOutlinedIcon, CheckIcon } from 'icons/materialIcons';
import { Box, Button, Typography, Menu, Avatar, CircularProgress, Divider } from '@mui/material';
import { getCardsByUser } from 'apis/userApi';
import { v4 as uuidv4 } from 'uuid';

const TeamCardListModal = ({ isOpen, onClose, onCreate, anchorEl, user, cardId, navigate }) => {
    const [teamMemberCards, setTeamMemberCards] = useState([]);
    const [loadingCards, setLoadingCards] = useState(false);
    const tempCardId = uuidv4();

    // Fetch team member's cards
    const fetchTeamMemberCards = useCallback(async () => {
        if (!user?.userId) return;

        setLoadingCards(true);
        try {
            console.log('🔍 Fetching cards for team member:', {
                userId: user.userId,
                email: user.email,
                fullName: user.fullName,
                name: user.name
            });
            const cards = await getCardsByUser(user.userId);
            console.log('✅ Cards fetched for team member:', {
                userId: user.userId,
                memberName: user.email || user.fullName,
                cardsCount: cards?.length || 0,
                cards: cards?.map(c => ({
                    id: c._id,
                    fullName: c.contact_info?.fullName,
                    isDefault: c.is_default,
                    profilePictureUrl: c.profilePictureUrl
                }))
            });
            setTeamMemberCards(cards);
        } catch (error) {
            console.error('❌ Failed to fetch team member cards:', error);
        } finally {
            setLoadingCards(false);
        }
    }, [user?.userId, user?.email, user?.fullName]);

    const handleCardSwitch = (newCardId) => {
        navigate(`/team/members/card/${newCardId}`);
        onClose();
        window.location.reload();
    };

    const handleCreateNewCard = () => {
        onClose();

        console.log('🎯 TeamCardListModal - Creating card for user:', {
            userId: user?.userId,
            email: user?.email,
            fullName: user?.fullName
        });

        // Pass the member's userId in navigation state so add-card page knows who to create the card for
        navigate(`/team/members/card/add-card/${tempCardId}`, {
            state: { memberUserId: user.userId, action: 'create' }
        });
        window.location.reload();
    };

    useEffect(() => {
        if (isOpen) {
            // Reset cards when modal opens to avoid stale data
            setTeamMemberCards([]);
            fetchTeamMemberCards();
        }
    }, [isOpen, user?.userId, fetchTeamMemberCards]);

    if (!isOpen) return null;


    return (
        <Menu
            anchorEl={anchorEl}
            open={isOpen}
            onClose={onClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
            }}
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.15)',
                    width: {
                        xs: '70vw',
                        sm: '320px',
                        md: '350px'
                    },
                    maxWidth: '350px',
                    maxHeight: '80vh', // Use viewport height instead of fixed height
                    overflow: 'hidden',
                    marginTop: '12px',
                    padding: '12px',
                    backgroundColor: '#fafafa'
                }
            }}
        >
            {loadingCards ? (
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '24px'
                }}>
                    <CircularProgress size={24} />
                    <Typography sx={{ ml: 2 }}>Loading cards...</Typography>
                </Box>
            ) : teamMemberCards.length === 0 ? (
                <Box sx={{
                    textAlign: 'center',
                    padding: '32px',
                    color: '#828282'
                }}>
                    <Typography>No cards found</Typography>
                </Box>
            ) : (
                <Box>
                    <Box sx={{
                        overflowY: 'auto',
                        maxHeight: '60vh', // Use viewport height for better responsiveness
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                        padding: '4px',
                        '&::-webkit-scrollbar': {
                            width: '6px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: '#f1f1f1',
                            borderRadius: '10px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: '#888',
                            borderRadius: '10px',
                            '&:hover': {
                                background: '#555',
                            }
                        }
                    }}>
                        {/* Viewing Card Section */}
                        {teamMemberCards.find(card => card._id === cardId) && (
                            <Box>
                                {/* Section Header */}
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    marginBottom: '8px',
                                    padding: '0 4px'
                                }}>
                                    <VisibilityOutlinedIcon sx={{
                                        fontSize: '16px',
                                        color: '#666',
                                        marginRight: '8px'
                                    }} />
                                    <Typography sx={{
                                        fontSize: '0.75rem',
                                        color: '#666',
                                        fontWeight: 500
                                    }}>
                                        Viewing
                                    </Typography>
                                </Box>

                                {/* Current Card */}
                                {teamMemberCards
                                    .filter(card => card._id === cardId)
                                    .map((card) => (
                                        <Box
                                            key={card._id}
                                            onClick={() => handleCardSwitch(card._id)}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                padding: '12px',
                                                backgroundColor: '#e3f2fd',
                                                borderRadius: '8px',
                                                cursor: 'pointer',
                                                minHeight: '60px',
                                                '&:hover': {
                                                    backgroundColor: '#e3f2fd'
                                                }
                                            }}
                                        >
                                            <Avatar
                                                src={card.profilePictureUrl}
                                                sx={{
                                                    width: 48,
                                                    height: 48,
                                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                                                }}
                                            />
                                            <Box sx={{
                                                marginLeft: 2,
                                                flex: 1,
                                                overflow: 'hidden',
                                                display: 'flex',
                                                flexDirection: 'column'
                                            }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                    <Typography
                                                        sx={{
                                                            fontWeight: 600,
                                                            fontSize: '1rem',
                                                            color: '#333',
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            flex: 1
                                                        }}
                                                    >
                                                        {card.contact_info?.fullName || 'Unnamed Card'}
                                                    </Typography>
                                                    {card.is_default && (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', marginLeft: '8px' }}>
                                                            <CheckIcon sx={{ fontSize: '14px', color: '#666' }} />
                                                            <Typography sx={{ fontSize: '0.75rem', color: '#666', fontWeight: 500 }}>
                                                                Default
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                                <Typography
                                                    sx={{
                                                        fontSize: '0.875rem',
                                                        color: '#666',
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis'
                                                    }}
                                                >
                                                    {`${card.contact_info?.jobTitle || ''}${card.contact_info?.jobTitle && card.contact_info?.company ? ' at ' : ''}${card.contact_info?.company || ''}`.trim() || 'No title'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ))}
                            </Box>
                        )}

                        {/* Other Cards Section */}
                        {teamMemberCards.filter(card => card._id !== cardId).length > 0 && (
                            <Box>
                                {/* Divider and spacing before Other Cards */}
                                <Divider sx={{ my: 2 }} />

                                {/* Section Header */}
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    marginBottom: '8px',
                                    padding: '0 4px'
                                }}>
                                    <ContactsOutlinedIcon sx={{
                                        fontSize: '16px',
                                        color: '#666',
                                        marginRight: '8px'
                                    }} />
                                    <Typography sx={{
                                        fontSize: '0.75rem',
                                        color: '#666',
                                        fontWeight: 500
                                    }}>
                                        Other Cards
                                    </Typography>
                                </Box>

                                {/* Other Cards */}
                                {teamMemberCards
                                    .filter(card => card._id !== cardId)
                                    .map((card) => (
                                        <Box
                                            key={card._id}
                                            onClick={() => handleCardSwitch(card._id)}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                padding: '12px',
                                                backgroundColor: '#fff',
                                                borderRadius: '8px',
                                                cursor: 'pointer',
                                                minHeight: '60px',
                                                marginBottom: '8px',
                                                '&:hover': {
                                                    backgroundColor: '#f4f4f4'
                                                }
                                            }}
                                        >
                                            <Avatar
                                                src={card.profilePictureUrl}
                                                sx={{
                                                    width: 48,
                                                    height: 48,
                                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                                                }}
                                            />
                                            <Box sx={{
                                                marginLeft: 2,
                                                flex: 1,
                                                overflow: 'hidden',
                                                display: 'flex',
                                                flexDirection: 'column'
                                            }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                    <Typography
                                                        sx={{
                                                            fontWeight: 600,
                                                            fontSize: '1rem',
                                                            color: '#333',
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            flex: 1
                                                        }}
                                                    >
                                                        {card.contact_info?.fullName || 'Unnamed Card'}
                                                    </Typography>
                                                    {card.is_default && (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', marginLeft: '8px' }}>
                                                            <CheckIcon sx={{ fontSize: '14px', color: '#666' }} />
                                                            <Typography sx={{ fontSize: '0.75rem', color: '#666', fontWeight: 500 }}>
                                                                Default
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                                <Typography
                                                    sx={{
                                                        fontSize: '0.875rem',
                                                        color: '#666',
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis'
                                                    }}
                                                >
                                                    {`${card.contact_info?.jobTitle || ''}${card.contact_info?.jobTitle && card.contact_info?.company ? ' at ' : ''}${card.contact_info?.company || ''}`.trim() || 'No title'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ))}
                            </Box>
                        )}
                    </Box>

                    <Divider sx={{ my: 1.5 }} />

                    <Box sx={{
                        padding: '8px 4px',
                        display: 'flex',
                        justifyContent: 'center'
                    }}>
                        <Button
                            variant="contained"
                            sx={{
                                backgroundColor: '#000',
                                color: '#fff',
                                fontWeight: 600,
                                borderRadius: '24px',
                                textTransform: 'none',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                padding: '10px 24px',
                                fontSize: '0.875rem',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                '&:hover': {
                                    backgroundColor: '#333',
                                    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
                                },
                                '& .MuiButton-startIcon': {
                                    marginRight: '6px',
                                    '& .MuiSvgIcon-root': {
                                        fontSize: '20px'
                                    }
                                }
                            }}
                            startIcon={<AddIcon />}
                            onClick={handleCreateNewCard}
                        >
                            Create New Card
                        </Button>
                    </Box>
                </Box>
            )}
        </Menu>
    );
};

export default TeamCardListModal;
